/**
 * The handful of icons the shell needs, inline.
 *
 * An icon package would add a dependency and a bundle chunk for eight glyphs.
 * These are 24x24 stroke outlines on the same grid, so they stay consistent
 * with each other; swap the set wholesale if a real icon library arrives.
 */

export type IconName =
  | 'home'
  | 'grid'
  | 'document'
  | 'check'
  | 'receipt'
  | 'scale'
  | 'sliders'
  | 'users'
  | 'menu'
  | 'close'
  | 'external'
  | 'clipboard'
  | 'user'
  | 'search'
  | 'plus'
  | 'logout'
  | 'bell'
  | 'building'
  | 'arrowLeft'
  | 'arrowRight'
  | 'undo'
  | 'apps'
  | 'chevronDown'
  | 'chevronsLeft'
  | 'pulse'
  | 'flag'
  | 'clock'
  | 'pen'
  | 'bolt'
  | 'lock'
  | 'capsLock'
  | 'wallet'
  | 'truck'
  | 'mail'
  | 'upload'
  | 'download'
  | 'eye'
  | 'trash'
  | 'tag'
  | 'chart'
  | 'alert'
  | 'send'
  | 'bank'
  | 'image'

const PATHS: Record<IconName, string> = {
  home: 'M3 10.5 12 3l9 7.5M5.25 9.75V20a1 1 0 0 0 1 1h3.5v-5.5h4.5V21h3.5a1 1 0 0 0 1-1V9.75',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  document: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h6',
  check: 'M20 6 9 17l-5-5',
  receipt: 'M6 3v18l3-2 3 2 3-2 3 2V3zM9.5 8h5M9.5 12h5M9.5 16h3',
  scale: 'M12 4v16M7 7 3 14h8zM17 7l-4 7h8zM7 7h10M5 20h14',
  sliders: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h10M18 18h2M14 4v4M8 10v4M14 16v4',
  users: 'M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 20v-2a4 4 0 0 0-3-3.87M16 2.13a4 4 0 0 1 0 7.75',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  external: 'M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  clipboard:
    'M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1ZM8 6H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-2M9 11h6M9 15h4',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M20.5 20.5 16 16',
  plus: 'M12 5v14M5 12h14',
  logout: 'M15 17l5-5-5-5M20 12H9M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6',
  bell: 'M18 9a6 6 0 1 0-12 0c0 3.5-.8 5.1-1.7 6h15.4c-.9-.9-1.7-2.5-1.7-6M10 19a2 2 0 0 0 4 0',
  building: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M2 21h20M8 8h3M8 12h3M8 16h3',
  arrowLeft: 'M19 12H5M11 18l-6-6 6-6',
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  undo: 'M4 9h11a5 5 0 0 1 0 10H9M4 9l4-4M4 9l4 4',
  // Nine small squares: the module switcher, and nothing else. Reusing it for
  // anything else would blur what the control means.
  apps: 'M4 4h3v3H4zM10.5 4h3v3h-3zM17 4h3v3h-3zM4 10.5h3v3H4zM10.5 10.5h3v3h-3zM17 10.5h3v3h-3zM4 17h3v3H4zM10.5 17h3v3h-3zM17 17h3v3h-3z',
  chevronDown: 'm6 9 6 6 6-6',
  chevronsLeft: 'm11 17-5-5 5-5M18 17l-5-5 5-5',
  pulse: 'M3 12h4l3-7 4 14 3-7h4',
  flag: 'M5 21V4h11l-2 4 2 4H5',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7v5l3 2',
  pen: 'M4 20h4L19 9l-4-4L4 16z',
  bolt: 'M13 2 4 14h7l-1 8 9-12h-7z',
  lock: 'M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2M8 11V8a4 4 0 0 1 8 0v3',
  capsLock: 'M12 4 4 12h4v5h8v-5h4zM8 20h8',
  wallet: 'M4 7a2 2 0 0 1 2-2h11v2M3 7h18v13H3zM16 13.5h2',
  truck: 'M3 6h11v10H3zM14 9h4l3 3v4h-7M7 19.3a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6M17 19.3a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6',
  mail: 'M3 5h18v14H3zM3 7l9 6 9-6',
  upload: 'M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  download: 'M12 4v12M7 11l5 5 5-5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 9.8a1.3 1.3 0 1 0 0-2.6 1.3 1.3 0 0 0 0 2.6',
  chart: 'M5 20v-9M10 20V5M15 20v-6M20 20V9M3 20h18',
  alert: 'M12 3 2 20h20zM12 10v4M12 17h.01',
  send: 'M21 3 10 14M21 3l-7 18-4-7-7-4z',
  bank: 'M3 10 12 4l9 6M5 10v8M9.7 10v8M14.3 10v8M19 10v8M3 20h18',
  image: 'M3 4h18v16H3zM9 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4M21 16l-5-5-9 9',
}

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className ?? 'size-[18px] shrink-0'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
